import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  getApiBaseUrl,
  GET_PASSWORD_POLICY,
  UPDATE_PASSWORD_POLICY,
  ADD_ACTIVITY,
} from "../Config/Config";

const PasswordPolicies = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const [getLoading, setGetLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [formData, setFormData] = useState({
    minLength: 0,
    maxLength: 72,

    requireUppercase: 0,
    requireLowercase: 0,
    requireNumber: 0,
    requireSpecialChar: 0,

    minUppercase: 0,
    minLowercase: 0,
    minDigits: 0,
    minSpecialChar: 0,

    expiryDays: 0,
    notifyBeforeDays: 0,
    preventReuseCount: 0,
    reuseFrequencyDays: 0,

    lockoutThreshold: 0,
    sessionTimeoutMinutes: 0,
  });
  const [showModal, setShowModal] = useState(false);
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? "" : Number(value),
    }));
  };

  const fetchPasswordPolicy = async () => {
    setGetLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}${GET_PASSWORD_POLICY}`);
      if (!res.ok) throw new Error("Failed to fetch");

      const data = await res.json();
      setFormData((prev) => ({
        ...prev,
        minLength: data?.ResultData?.MinLength || 0,
        requireUppercase: data?.ResultData?.RequireUppercase || 0,
        requireLowercase: data?.ResultData?.RequireLowercase || 0,
        requireNumber: data?.ResultData?.RequireDigit || 0,
        requireSpecialChar: data?.ResultData?.RequireSpecialChar || 0,
        expiryDays: data?.ResultData?.ExpiryDays || 0,
        lockoutThreshold: data?.ResultData?.MaxFailedAttempts || 0,
        preventReuseCount: data?.ResultData?.ReuseHistoryCount || 0,
        sessionTimeoutMinutes: data?.ResultData?.sessionTimeoutMinutes || 0,
        notifyBeforeDays: data?.ResultData?.notifyBeforeDays || 0,
      }));
    } catch (error) {
      console.error("Error fetching policy:", error);
    } finally {
      setGetLoading(false);
    }
  };

  useEffect(() => {
    fetchPasswordPolicy();
  }, []);

  const handleUpdate = async (userPassword) => {
    setPostLoading(true);

    const loginPayload = {
      username: user?.UserName,
      password: userPassword,
    };

    const logActivity = async (isSuccess, note) => {
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ActivityType: 1,
          PerformedBy: user.UserID,
          PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
          Notes: note,
          Location: "addLocation",
          IsSucces: isSuccess ? 1 : 0,
        }),
      });
    };

    const payload = {
      MinLength: formData.minLength,
      RequireUppercase: formData.requireUppercase,
      RequireLowercase: formData.requireLowercase,
      RequireDigit: formData.requireNumber,
      RequireSpecialChar: formData.requireSpecialChar,
      ExpiryDays: formData.expiryDays,
      MaxFailedAttempts: formData.lockoutThreshold,
      ReuseHistoryCount: formData.preventReuseCount,
      LockoutDurationMinutes: formData.lockoutDuration,
      sessionTimeoutMinutes: formData.sessionTimeoutMinutes,
    };

    try {
      // 1️⃣ First login check
      const loginRes = await fetch(`${getApiBaseUrl()}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginPayload),
      });

      const loginData = await loginRes.json();

      if (!loginRes.ok || !loginData?.status) {
        // ❌ Stop here if login fails
        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: loginData?.Message || "Invalid credentials.",
        });
        return; // 👈 Exit early
      }

      // 2️⃣ Only if login succeeded, update policy
      const res = await fetch(`${getApiBaseUrl()}${UPDATE_PASSWORD_POLICY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (res.ok && result?.status) {
        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: result.message || "Password policy updated successfully.",
          confirmButtonText: "OK",
        }).then(() => {
          fetchPasswordPolicy();
        });

        await logActivity(true, "Password policy updated.");
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: result?.message || "Failed to update password policy.",
        });

        await logActivity(false, "Password policy update failed.");
      }
    } catch (error) {
      console.error("Update failed:", error);

      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong. Please try again.",
      });
    } finally {
      setPostLoading(false);
    }
  };

  const handleSubmitPassword = () => {
    setShowModal(false);
    handleUpdate(password);
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  return (
    <>
      {postLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)", // subtle tint
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/assetmgt/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item " aria-current="page">
                      Password Policies
                    </h4>
                  </ol>
                </nav>
                <h6>Manage Your Password Policies</h6>
              </div>
            </div>
            <div className="page-btn">
              <button
                className="btn btn-sm btn-primary"
                title="Save Changes"
                type="submit"
                disabled={postLoading}
                onClick={() => setShowModal(true)}
              >
                <i className="fa fa-check fs-4 "></i>
              </button>
            </div>
          </div>
          {getLoading ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              />
            </div>
          ) : (
            <div className="">
              <form className="row">
                <div className="col-12 col-xxl-6 d-flex flex-column">
                  <div className="card shadow-sm mb-4 h-100">
                    <div className="card-header bg-primary text-white py-2">
                      <h5 className="mb-0 text-white">Password Complexity</h5>
                    </div>
                    <div className="card-body p-2">
                      <div className="row">
                        <div className="col-2 mb-3">
                          <label>Min Length</label>
                          <input
                            type="number"
                            name="minLength"
                            className="form-control"
                            value={formData.minLength}
                            onChange={handleChange}
                            disabled={postLoading}
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                        {/* <div className="col-2 mb-3">
                        <label>Max Length</label>
                        <input
                          type="number"
                          name="maxLength"
                          className="form-control"
                          value={formData.maxLength}
                          onChange={handleChange}
                          disabled={postLoading}
                          onWheel={(e) => e.target.blur()}
                        />
                      </div> */}
                        <div className="col-2 mb-3">
                          <label>Min Uppercase Letters</label>
                          <input
                            type="number"
                            name="requireUppercase"
                            className="form-control"
                            value={formData.requireUppercase}
                            onChange={handleChange}
                            disabled={postLoading}
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                        <div className="col-2 mb-3">
                          <label>Min Lowercase Letters</label>
                          <input
                            type="number"
                            name="requireLowercase"
                            className="form-control"
                            value={formData.requireLowercase}
                            onChange={handleChange}
                            disabled={postLoading}
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                        <div className="col-2 mb-3">
                          <label>Min Digits</label>
                          <input
                            type="number"
                            name="requireNumber"
                            className="form-control"
                            value={formData.requireNumber}
                            onChange={handleChange}
                            disabled={postLoading}
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                        <div className="col-2 mb-3">
                          <label>Min Special Characters</label>
                          <input
                            type="number"
                            name="requireSpecialChar"
                            className="form-control"
                            value={formData.requireSpecialChar}
                            onChange={handleChange}
                            disabled={postLoading}
                            onWheel={(e) => e.target.blur()}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-7 col-xxl-auto d-flex flex-column">
                  <div className="card shadow-sm mb-4 h-100">
                    <div className="card-header bg-primary text-white py-2">
                      <h5 className="mb-0 text-white">
                        Password Expiry & Reuse
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-auto mb-3">
                          <label>Password Expiry</label>
                          <div className="input-group">
                            <input
                              type="number"
                              name="expiryDays"
                              className="form-control"
                              style={{ width: "100px" }}
                              value={formData.expiryDays}
                              onChange={handleChange}
                              disabled={postLoading}
                              onWheel={(e) => e.target.blur()}
                            />
                            <span className="input-group-text">Days</span>
                          </div>
                        </div>

                        <div className="col-auto mb-3">
                          <label>Notify Before Expiry</label>
                          <div className="input-group">
                            <input
                              type="number"
                              name="notifyBeforeDays"
                              className="form-control"
                              style={{ width: "100px" }}
                              value={formData.notifyBeforeDays}
                              onChange={handleChange}
                              disabled={postLoading}
                              onWheel={(e) => e.target.blur()}
                            />
                            <span className="input-group-text">Days</span>
                          </div>
                        </div>

                        <div className="col-auto mb-3">
                          <label>Password Reuse Limit</label>
                          <div className="input-group">
                            <input
                              type="number"
                              name="preventReuseCount"
                              className="form-control"
                              style={{ width: "100px" }}
                              value={formData.preventReuseCount}
                              onChange={handleChange}
                              disabled={postLoading}
                              onWheel={(e) => e.target.blur()}
                            />
                            <span className="input-group-text">Times</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Lockout */}
                <div className="col-5 col-xxl-auto d-flex flex-column">
                  <div className="card shadow-sm mb-4 h-100">
                    <div className="card-header bg-primary text-white py-2">
                      <h5 className="mb-0 text-white">Session & Lockout</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-auto mb-3">
                          <label>Max Failed Attempts</label>
                          <div className="input-group">
                            <input
                              type="number"
                              name="lockoutThreshold"
                              className="form-control"
                              value={formData.lockoutThreshold}
                              onChange={handleChange}
                              disabled={postLoading}
                              style={{ width: "100px" }}
                              onWheel={(e) => e.target.blur()}
                            />
                            <span className="input-group-text">Times</span>
                          </div>
                        </div>

                        <div className="col-auto mb-3">
                          <label>Session Expiry</label>
                          <div className="input-group">
                            <input
                              type="number"
                              name="sessionTimeoutMinutes"
                              className="form-control"
                              value={formData?.sessionTimeoutMinutes}
                              style={{ width: "100px" }}
                              disabled={postLoading}
                              onChange={handleChange}
                              onWheel={(e) => e.target.blur()}
                            />
                            <span className="input-group-text">Mins</span>
                          </div>
                        </div>

                        {/* <div className="col-auto mb-3">
                      <div className="form-check mt-4">
                        <input
                          type="checkbox"
                          name="adminUnlockOnly"
                          className="form-check-input"
                          checked={formData.adminUnlockOnly}
                          onChange={handleChange}
                          id="adminUnlockOnly"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="adminUnlockOnly"
                        >
                          Only Admin Can Unlock
                        </label>
                      </div>
                    </div> */}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Session Timeout */}
                {/* <div className="col-auto d-flex flex-column">
              <div className="card shadow-sm mb-4 h-100">
                <div className="card-header bg-primary text-white py-2">
                  <h5 className="mb-0 text-white">Session Timeout</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-auto mb-3">
                      <label>Inactivity Timeout</label>
                      <div className="input-group">
                        <input
                          type="number"
                          name="sessionTimeout"
                          className="form-control"
                          value={formData.sessionTimeout}
                          onChange={handleChange}
                          style={{ width: "100px" }}
                          onWheel={(e) => e.target.blur()}
                        />
                        <span className="input-group-text">Mins</span>
                      </div>
                    </div>

                    <div className="col-auto mb-3">
                      <label>Warning Before Logout</label>
                      <div className="input-group">
                        <input
                          type="number"
                          name="warningBeforeLogout"
                          className="form-control"
                          value={formData.warningBeforeLogout}
                          onChange={handleChange}
                          style={{ width: "100px" }}
                          onWheel={(e) => e.target.blur()}
                        />
                        <span className="input-group-text">Seconds</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}

                {/* Submit Button */}
                {/* <div className="text-end">
    <button type="submit" className="btn btn-primary px-4">
      Save Settings
    </button>
  </div> */}
              </form>
            </div>
          )}
        </div>
        {showModal && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          >
            <div className="modal-dialog modal-md">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Password</h5>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowModal(false)}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="pass-group">
                    <input
                      type={isPasswordVisible ? "text" : "password"}
                      name="password"
                      className="pass-input form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      onCopy={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      placeholder="Enter Password"
                    />
                    <span
                      className={`ti toggle-password ${
                        isPasswordVisible ? "ti-eye" : "ti-eye-off"
                      }`}
                      onClick={togglePasswordVisibility}
                    ></span>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary me-2"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmitPassword}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>{" "}
    </>
  );
};

export default PasswordPolicies;
